import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChessPawn } from '@fortawesome/free-solid-svg-icons';
import TitleBadge from './TitleBadge';

const FeaturedWidget = ({ title, subtitle, icon, link, slug, onClick, isNew = false, size = 'normal', backgroundImage = null, backgroundColor = null, titled = false, chessTitle = '' }) => {
  const navigate = useNavigate();
  const isEmoji = icon && typeof icon === 'string' && icon.length <= 2 && !icon.includes('/');
  const hasBackgroundImage = backgroundImage || (!isEmoji && icon && typeof icon === 'string' && icon.includes('/'));
  const backgroundUrl = backgroundImage || icon;
  const hasCustomBackground = backgroundColor || hasBackgroundImage;

  const handleClick = () => {
    if (onClick) { onClick(); return; }
    // Slug takes priority — navigate to internal detail page
    if (slug) {
      navigate(`/${slug}`);
      return;
    }
    if (!link) return;

    // Check if it's an internal link (starts with /)
    if (link.startsWith('/')) {
      navigate(link);
    } else {
      // External link
      window.open(link, '_blank');
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.2 }}
      className={`
        ${hasBackgroundImage ? 'bg-cover bg-center bg-no-repeat' : (backgroundColor ? '' : 'bg-gray-800')}
        ${hasCustomBackground ? 'border-4 border-gray-600' : 'border border-gray-700'}
        rounded-3xl 
        w-full
        aspect-square
        flex 
        flex-col
        items-center 
        justify-center
        cursor-pointer
        ${hasCustomBackground ? 'hover:border-gray-500' : 'hover:border-gray-600'}
        hover:scale-105
        transition-all
        duration-200
        shadow-lg
        relative
        overflow-hidden
        ${hasCustomBackground ? 'p-0' : 'p-6'}
      `}
      style={
        backgroundColor ? { 
          backgroundColor: backgroundColor,
          backgroundClip: 'padding-box'
        } : hasBackgroundImage ? { 
          backgroundImage: `url(${backgroundUrl})`,
          backgroundClip: 'padding-box'
        } : {}
      }
      onClick={handleClick}
    >
      {/* NEW Ribbon Banner */}
      {isNew && (
        <div 
          className="absolute text-white font-bold shadow-lg z-10"
          style={{
            top: size === 'normal' ? '6px' : '8px',
            right: size === 'normal' ? '-16px' : '-22px',
            transform: 'rotate(45deg)',
            transformOrigin: 'center',
            background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
            boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
            width: size === 'normal' ? '60px' : '75px',
            textAlign: 'center',
            padding: size === 'normal' ? '2px 0' : '3px 0',
            fontSize: size === 'normal' ? '8px' : '9px'
          }}
        >
          NEW
        </div>
      )}
      
      {/* Content */}
      {(hasBackgroundImage || backgroundColor) && size === 'double' ? (
        /* Background only - no content for large widgets */
        null
      
      ) : (hasBackgroundImage || backgroundColor) && size === 'normal' ? (
        /* Background with NO text overlay for small widgets */
        null
      ) : (
        /* Regular centered content for emojis */
        <div className="flex flex-col items-center justify-center text-center h-full">
          {/* Icon */}
          <div className={size === 'normal' ? "mb-2" : "mb-3"}>
            {isEmoji ? (
              <div className={size === 'normal' ? "text-3xl" : "text-5xl"}>{icon}</div>
            ) : (
              <img src={icon} alt={title} className={size === 'normal' ? "w-10 h-10 object-contain" : "w-16 h-16 object-contain"} />
            )}
          </div>
          
          {/* Title */}
          <div className={size === 'normal' ? "text-sm font-bold text-gray-100 mb-0.5" : "text-base font-bold text-gray-100 mb-1"}>
            <TitleBadge titled={titled} chessTitle={chessTitle} size="sm" />{title}
          </div>
          
          {/* Subtitle */}
          {subtitle && (
            <div className={size === 'normal' ? "text-xs text-gray-400" : "text-sm text-gray-400"}>
              {subtitle}
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default FeaturedWidget;